/** Configuração do poller. Todos os tempos em milissegundos. */
export type RealtimeFallbackOptions = {
	/** Busca por REST o estado que o socket deveria ter entregue. */
	resync: () => void
	/** O canal está CONECTADO? Conectado, o poller dorme: o socket é o canal primário. */
	isConnected: () => boolean
	/** Intervalo entre buscas enquanto o canal está fora. Padrão: 8s. */
	intervalMs?: number
	/** A aba está oculta? Injetável para teste. */
	isHidden?: () => boolean
}

/**
 * Polling de escape para quando o tempo real CAI.
 *
 * O socket é o canal primário desta interação — silenciosa e em tempo real. O poller só entra sem
 * conexão: enquanto o STOMP.js tenta reconectar (`reconnectDelay`), a tela segue viva por REST;
 * quando a conexão volta, o re-sync de reconexão dos componentes recupera o que passou fora do ar e
 * este ciclo volta a dormir.
 *
 * Até 24/09/2026 a régua era o SILÊNCIO do canal (tempo desde o último frame), herança do broker em
 * memória por pod, em que um socket conectado podia estar mudo. Com o relay para o RabbitMQ esse
 * modo de falha deixou de existir e a régua virou um gerador de carga: numa sessão sem lances o
 * silêncio é o normal, então cada tela batia no servidor a cada 8s com o socket perfeitamente
 * conectado (`notice-session-phase-bids/search`, `messages`, `presence`...). Foi o consumo indevido
 * relatado na tela do participante.
 *
 * O que continua valendo:
 *
 * - **Pausa com a aba oculta.** Ninguém está olhando; ao voltar, sincroniza IMEDIATAMENTE — é o
 *   momento em que o usuário mais precisa da tela correta, e o navegador pode ter congelado a aba
 *   sem que o cliente STOMP tenha percebido ainda.
 * - **Nunca empilha requisição.** Uma busca em voo por vez; o tick que cair no meio é descartado.
 * - **Jitter de ±15%**, para as abas abertas na mesma sessão não baterem no servidor juntas.
 *
 * Com o canal conectado o tick continua rodando, mas só para conferir a conexão — custa zero rede.
 */
export class RealtimeFallbackPoller {
	private handle?: ReturnType<typeof setTimeout>
	private running = false
	private inFlight = false
	private visibilityListener?: () => void

	private readonly intervalMs: number
	private readonly isHidden: () => boolean

	constructor(private readonly options: RealtimeFallbackOptions) {
		this.intervalMs = options.intervalMs ?? 8_000
		this.isHidden = options.isHidden ?? (() => typeof document !== 'undefined' && document.hidden)
	}

	start(): void {
		if (this.running) return

		this.running = true
		this.attachVisibility()
		this.schedule()
	}

	stop(): void {
		this.running = false

		if (this.handle) {
			clearTimeout(this.handle)
			this.handle = undefined
		}

		this.detachVisibility()
	}

	/** Busca agora, fora do ciclo — usado ao voltar para a aba. */
	syncNow(): void {
		if (!this.running || this.inFlight) return

		this.runResync()
		this.schedule()
	}

	/** Informa que a busca terminou; até isso acontecer, nenhum tick dispara outra. */
	settle(): void {
		this.inFlight = false
	}

	private schedule(): void {
		if (!this.running) return

		if (this.handle) clearTimeout(this.handle)

		this.handle = setTimeout(() => this.tick(), this.nextDelay())
	}

	private tick(): void {
		if (!this.running) return

		// Aba oculta: nada a mostrar — o retorno dispara `syncNow` pelo listener de visibilidade.
		// Canal conectado: o socket está entregando — buscar por REST aqui seria só carga.
		if (this.isHidden() || this.options.isConnected()) {
			this.schedule()
			return
		}

		this.runResync()
		this.schedule()
	}

	private runResync(): void {
		if (this.inFlight) return

		this.inFlight = true

		try {
			this.options.resync()
		} catch {
			// Uma busca que falha não pode matar o ciclo — o próximo tick tenta de novo.
			this.inFlight = false
		}
	}

	private nextDelay(): number {
		const jitter = this.intervalMs * 0.15

		return Math.round(this.intervalMs - jitter + Math.random() * jitter * 2)
	}

	private attachVisibility(): void {
		if (typeof document === 'undefined' || this.visibilityListener) return

		this.visibilityListener = () => {
			if (!this.isHidden()) this.syncNow()
		}

		document.addEventListener('visibilitychange', this.visibilityListener)
	}

	private detachVisibility(): void {
		if (typeof document === 'undefined' || !this.visibilityListener) return

		document.removeEventListener('visibilitychange', this.visibilityListener)
		this.visibilityListener = undefined
	}
}
